'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, M, MB, P, Quiz, Step, UL } from '../_components/lesson'
import * as EX from './exercises/lang-extra'

const RNNUnroll = dynamic(() => import('../_components/widgets/SequenceWidgets').then(m => m.RNNUnroll), { ssr: false })

export default function RecurrentNetworks() {
  return (
    <Lesson>
      <Step>
        <H>A network with a memory</H>
        <P>
          The neural language model reads a fixed window of 3 characters. Anything earlier is simply invisible. Text needs
          more: whether “bank” means a river or money can depend on words much further back.
        </P>
        <P>
          A <strong>recurrent neural network</strong> (RNN) reads one token at a time and carries a <strong>hidden state</strong>{' '}
          forward: a vector that summarises everything read so far. At each step it mixes the new input with its old state:
        </P>
        <MB>{'\\mathbf{h}_t = \\tanh(\\mathbf{x}_t W_x + \\mathbf{h}_{t-1} W_h + \\mathbf{b})'}</MB>
        <P>
          The same weights are used at every step, so it can read sequences of any length.
        </P>
        <Figure n="8.7" wide caption="The RNN unrolled: one column per letter. Each hidden state is built from the letter below it and the state to its left. Type your own word.">
          <RNNUnroll />
        </Figure>
      </Step>

      <Step gate="rnn-forward">
        <H>Write the loop</H>
        <Exercise
          id="rnn-forward"
          title="rnn"
          {...EX.rnnForward}
          hints={[
            <>
              <C>{'let h = new Array(b.length).fill(0)'}</C>, then for each <C>x</C>: compute{' '}
              <C>{'mat.apply(mat.transpose(Wx), x)'}</C> and <C>{'mat.apply(mat.transpose(Wh), h)'}</C>, add them and{' '}
              <C>b</C>, and take <C>Math.tanh</C> of each.
            </>,
          ]}
        />
      </Step>

      <Step gate="char-rnn">
        <H>Train one on names</H>
        <P>
          To train an RNN, <strong>unroll</strong> it: run it for T steps, predicting the next character at each one, and add
          up the losses. The unrolled RNN is just a deep network with shared weights, so <C>.backward()</C> works through all the
          steps at once. That’s called <strong>backpropagation through time</strong>.
        </P>
        <Exercise
          id="char-rnn" timeoutMs={60000}
          title="batchLoss"
          {...EX.charRnn}
          hints={[
            <>
              <C>{'let h = Tensor.zeros(starts.length, H)'}</C> and a list of losses. For <C>t</C> from 0 to T − 1:{' '}
              <C>{'h = step(starts.map(p => trainStream[p + t]), h)'}</C>.
            </>,
            <>
              Then push <C>{'head.forward(h).crossEntropy(starts.map(p => trainStream[p + t + 1]))'}</C>. At the end:{' '}
              <C>{'losses.reduce((a, b) => a.add(b)).div(T)'}</C>.
            </>,
          ]}
        />
      </Step>

      <Step gate="q-vanish">
        <H>The trouble with long memories</H>
        <P>
          Go back to the figure and look at the pink bars: how much the last state still depends on each earlier letter. Each
          step back multiplies the gradient by <M>{'W_h'}</M> and by tanh’s slope. Multiply by something below 1 often enough
          and it vanishes; above 1, and it explodes. Drag the slider and watch.
        </P>
        <Quiz
          id="q-vanish"
          question="Why do plain RNNs struggle to use information from 50 steps ago?"
          options={[
            { label: 'The gradient is multiplied at every step on the way back, so it shrinks (or blows up) exponentially', correct: true },
            { label: 'The hidden state is deleted every 10 steps', why: 'Nothing is deleted. The influence just fades as it’s multiplied over and over.' },
            { label: 'They only have 3 characters of context', why: 'That’s the fixed-window MLP. An RNN’s state can in principle carry anything forward.' },
          ]}
          explain="It’s the deep-network problem from Module 7 again, because an unrolled RNN is a very deep network."
        />
        <UL>
          <li>
            <strong>LSTMs</strong> (1997) and <strong>GRUs</strong> (2014) add <em>gates</em>: small sigmoid switches that decide
            what to forget, what to write and what to read. The memory is updated by addition, like a residual connection, so
            gradients survive much longer. LSTMs powered translation and speech recognition through the 2010s.
          </li>
          <li>
            But RNNs read strictly one token after another, which is slow on GPUs built for parallel work. And their memory is
            still one fixed-size vector.
          </li>
        </UL>
        <Callout kind="why">
          In 2017, the transformer threw the recurrence away. Instead of passing a memory along the sequence, every token looks
          directly at every other token, all in parallel. That mechanism is <strong>attention</strong>, and it’s Module 9.
        </Callout>
      </Step>
    </Lesson>
  )
}
